import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ArrowBack, Timer, AccessTime } from "@mui/icons-material";
import api from "../../api/axios";

/* ── Helpers ── */
function formatCurrency(value) {
  if (typeof value === "number") return `₹${value.toLocaleString("en-IN")}`;
  if (value === 0) return "₹0";
  return value || "—";
}

function formatDuration(ms) {
  if (!ms || ms < 0) return "—";
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return `${min}m ${sec}s`;
  const hrs = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hrs}h ${remMin}m ${sec}s`;
}

function formatDurationShort(ms) {
  if (!ms || ms < 0) return "—";
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  if (min < 60) return `${min}m ${totalSec % 60}s`;
  const hrs = Math.floor(min / 60);
  return `${hrs}h ${min % 60}m`;
}

function getSpeedColor(ms) {
  if (!ms) return "#94a3b8";
  const sec = ms / 1000;
  if (sec < 10) return "#16a34a";
  if (sec < 30) return "#f59e0b";
  return "#ef4444";
}

function getSpeedLabel(ms) {
  if (!ms) return "—";
  const sec = ms / 1000;
  if (sec < 10) return "⚡ Fast";
  if (sec < 30) return "⏳ Moderate";
  return "🐢 Slow";
}

const CARD_ACCENTS = {
  totalAccepted: { icon: "#0891b2", bg: "rgba(8,145,178,0.10)", border: "#cffafe" },
  avgAcceptTime: { icon: "#0891b2", bg: "rgba(8,145,178,0.10)", border: "#cffafe" },
  fastestAccept: { icon: "#16a34a", bg: "rgba(22,163,74,0.10)", border: "#dcfce7" },
  slowestAccept: { icon: "#ef4444", bg: "rgba(220,38,38,0.10)", border: "#fee2e2" },
};

const LIMIT = 20;

function extractAcceptedEntries(orders) {
  const result = [];
  orders.forEach((order) => {
    const notifs = order.agentNotifications || [];
    notifs.forEach((notif) => {
      if (notif.status !== "ACCEPTED") return;
      if (!notif.sentAt || !notif.acceptedAt) return;
      const diff = new Date(notif.acceptedAt) - new Date(notif.sentAt);
      if (diff <= 0) return;
      result.push({
        orderId: order._id,
        orderIdShort: order._id?.substring(0, 8).toUpperCase(),
        total: order.total,
        sentAt: notif.sentAt,
        acceptedAt: notif.acceptedAt,
        acceptTimeMs: diff,
      });
    });
  });
  result.sort((a, b) => a.acceptTimeMs - b.acceptTimeMs);
  return result;
}

export default function AgentAvgAcceptTime() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchPage = useCallback(async (pageNum) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
      const { data } = await api.get(`/agent/orders/accepted?${params}`);
      const newOrders = data.orders || [];

      setAllOrders((prev) => pageNum === 1 ? newOrders : [...prev, ...newOrders]);
      setPage(pageNum);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const handleLoadMore = useCallback(() => {
    fetchPage(page + 1);
  }, [page, fetchPage]);

  const entries = useMemo(() => extractAcceptedEntries(allOrders), [allOrders]);
  const hasMore = allOrders.length < totalCount;

  const stats = useMemo(() => {
    if (entries.length === 0) {
      return { count: 0, avg: null, fastest: null, slowest: null };
    }
    const totalMs = entries.reduce((sum, e) => sum + e.acceptTimeMs, 0);
    return {
      count: entries.length,
      avg: totalMs / entries.length,
      fastest: entries[0].acceptTimeMs,
      slowest: entries[entries.length - 1].acceptTimeMs,
    };
  }, [entries]);

  const kpiCards = useMemo(() => [
    { id: "totalAccepted", title: "Total Accepted", value: stats.count },
    { id: "avgAcceptTime", title: "Avg Accept Time", value: stats.avg ? formatDurationShort(stats.avg) : "—" },
    { id: "fastestAccept", title: "Fastest Accept", value: stats.fastest ? formatDurationShort(stats.fastest) : "—" },
    { id: "slowestAccept", title: "Slowest Accept", value: stats.slowest ? formatDurationShort(stats.slowest) : "—" },
  ], [stats]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Button onClick={() => navigate("/agent/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBack sx={{ fontSize: 16 }} />
          Back to Dashboard
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>⏱ Avg Accept Time Details</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>Detailed breakdown of how quickly you accept orders</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1200, mx: "auto" }}>
        {/* KPI Cards */}
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, alignItems: "stretch", mb: 3 }}>
          {kpiCards.map((c) => {
            const accent = CARD_ACCENTS[c.id] || CARD_ACCENTS.totalAccepted;
            return (
              <Box key={c.id} sx={{ p: 1.25, borderRadius: "12px", bgcolor: "#fff", border: `1.5px solid ${accent.border}`, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.25 }, width: "100%", boxSizing: "border-box", minHeight: { xs: 80, sm: 80, md: 90 } }}>
                <Box sx={{ width: { xs: 34, sm: 38, lg: 36 }, height: { xs: 34, sm: 38, lg: 36 }, borderRadius: "10px", bgcolor: accent.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <AccessTime sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", lg: "0.68rem" }, fontWeight: 500, color: "#64748b", lineHeight: 1.2, mb: 0.4, overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</Typography>
                  {loading ? (
                    <Box sx={{ width: "70%", height: 24, bgcolor: "#e2e8f0", borderRadius: 1 }} />
                  ) : (
                    <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem", lg: "1rem" }, fontWeight: 700, lineHeight: 1, color: "#0f172a" }}>{c.value}</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Speed Distribution */}
        {!loading && entries.length > 0 && stats.avg && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1e293b", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <Timer sx={{ fontSize: 18, color: "#0891b2" }} />
              Acceptance Speed Distribution
            </Typography>
            {(() => {
              const fast = entries.filter((e) => e.acceptTimeMs < 10000).length;
              const moderate = entries.filter((e) => e.acceptTimeMs >= 10000 && e.acceptTimeMs < 30000).length;
              const slow = entries.filter((e) => e.acceptTimeMs >= 30000).length;
              const total = entries.length || 1;
              return (
                <Box>
                  <Box sx={{ display: "flex", borderRadius: 1.5, overflow: "hidden", height: 28, mb: 1 }}>
                    {fast > 0 && <Box sx={{ width: `${(fast / total) * 100}%`, bgcolor: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{fast}</Typography></Box>}
                    {moderate > 0 && <Box sx={{ width: `${(moderate / total) * 100}%`, bgcolor: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{moderate}</Typography></Box>}
                    {slow > 0 && <Box sx={{ width: `${(slow / total) * 100}%`, bgcolor: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{slow}</Typography></Box>}
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#16a34a" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>{'Fast (<10s)'}</Typography></Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#f59e0b" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Moderate (10-30s)</Typography></Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#ef4444" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>{'Slow (>30s)'}</Typography></Box>
                  </Box>
                </Box>
              );
            })()}
          </Paper>
        )}

        {/* Details Table */}
        <Paper sx={{ borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTime sx={{ fontSize: 18, color: "#0891b2" }} />
              Accept Time Details ({allOrders.length} of {totalCount} orders loaded)
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={32} sx={{ color: "#0891b2", mb: 1.5 }} /><Typography variant="body2" sx={{ color: "#94a3b8" }}>Loading acceptance data...</Typography></Box>
          ) : entries.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(8,145,178,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}><Timer sx={{ fontSize: 32, color: "#0891b2" }} /></Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1e293b", mb: 0.5 }}>No acceptance data</Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>Accept some orders to see your metrics here.</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Sent At</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Accepted At</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Accept Time</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Speed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry, idx) => (
                      <TableRow key={`${entry.orderId}-${idx}`} sx={{ "&:hover": { bgcolor: "#f8fafc" }, cursor: "pointer" }} onClick={() => navigate(`/agent/orders/${entry.orderId}`)}>
                        <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1976d2", fontSize: "0.78rem" }}>#{entry.orderIdShort}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{formatCurrency(entry.total)}</Typography></TableCell>
                        <TableCell><Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem" }}>{new Date(entry.sentAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</Typography></TableCell>
                        <TableCell><Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem" }}>{new Date(entry.acceptedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight={700} sx={{ color: getSpeedColor(entry.acceptTimeMs), fontSize: "0.78rem" }}>{formatDuration(entry.acceptTimeMs)}</Typography></TableCell>
                        <TableCell><Chip label={getSpeedLabel(entry.acceptTimeMs)} size="small" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 22, bgcolor: `${getSpeedColor(entry.acceptTimeMs)}15`, color: getSpeedColor(entry.acceptTimeMs), border: `1px solid ${getSpeedColor(entry.acceptTimeMs)}30` }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Load More */}
              {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 4 }}>
                    {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}